import { IUser, Role } from '@/model/IUser';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {csrfStorage} from '@/utils/csrfStorage.ts';

interface UserContextProps {
  user: IUser | undefined;
  setUser: React.Dispatch<React.SetStateAction<IUser | undefined>>;
  claims: Role[];
  setClaims: React.Dispatch<React.SetStateAction<Role[]>>;
  roles: Role[];
  setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
  csrfTokenFallback: string;
  setCsrfTokenFallback: React.Dispatch<React.SetStateAction<string>>;
}

export const UserContext = createContext<UserContextProps | undefined>(undefined);

export function UserProvider({user, setUser, children }) {
  const [claims, setClaims] = useState<Role[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [csrfTokenFallback, _setCsrfTokenFallback] = useState<string | null>(null);

  const setCsrfTokenFallback = (newToken: string | null) => {
    csrfStorage.setToken(newToken);
    _setCsrfTokenFallback(newToken);
  };

  useEffect(() => {
    if (user && user.roles) {
      setUser(user);
      setClaims(user.roles);
    }
  }, [user]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        claims,
        setClaims,
        roles,
        setRoles,
        csrfTokenFallback,
        setCsrfTokenFallback
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
    return useContext(UserContext);
}

export function isUserInClaim(claim) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ctx = useContext(UserContext);

    if (ctx && ctx.claims) {
        const userRoles = ctx.claims;
        const isAdmin = CheckIsAdmin() || CheckIsSuperAdmin();

        if (isAdmin) return true;

        const hasClaim = userRoles.find(function (x) {
            return x.name == claim || x.systemName == claim;
        });
        return !!hasClaim;
    }
    return false;
}

export function IsBroker() {
    return isUserInClaim('BrokerUser');
}

export function CheckIsClient() {
    const ctx = useContext(UserContext);
    return ctx.user && ctx.user.userIsClient;
}

export function CheckIsAdmin() {
    const ctx = useContext(UserContext);
    if (ctx && ctx.claims) {
        const userRoles = ctx.claims;
        const isAdmin = userRoles.find(function (x) {
            return x.name == 'Administrator' || x.systemName == 'Administrator';
        });

        return !!isAdmin;
    }

    return false;
}

export function CheckIsSuperAdmin() {
    const ctx = useContext(UserContext);
    if (ctx && ctx.claims) {
        const userRoles = ctx.claims;
        const isAdmin = userRoles.find(function (x) {
            return x.name == 'SuperAdminClient' || x.systemName == 'SuperAdminClient';
        });

        return !!isAdmin;
    }

    return false;
}
